<template>
  <v-dialog
    :model-value="open"
    content-class="bingo-category-venues-modal"
    fullscreen
    persistent
    scroll-strategy="none"
    transition="dialog-bottom-transition"
    :scrim="'#212121'"
    :opacity="0.65"
    @update:model-value="$emit('update:open', $event)"
  >
    <v-card class="bingo-category-venues-modal d-flex flex-column">
      <v-card-title class="pa-5 pb-0 flex-column v-header--fixed flex-shrink-0">
        <div class="bingo-category-venues-modal__header d-flex justify-space-between align-center">
          <h4 class="bingo-category-venues-modal__title mb-2">{{ categoryName || 'Select a venue' }}</h4>
          <v-btn icon variant="text" class="flex-shrink-0" @click="$emit('update:open', false)">
            <icon height="14" width="14">close-x</icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text class="bingo-category-venues-modal__body mt-1 py-0">
        <div class="bingo-category-venues-modal__scroll">

          <v-alert
            type="error"
            variant="outlined"
            v-if="categoryUnavailable"
            class="bingo-category-venues-modal__unavailable-notice"
          >
          <template #prepend>
            <icon fill="error" :height="16">Lock</icon>
          </template>

            <v-alert-title class="bingo-category-venues-modal__unavailable-notice-title">Currently Unavailable</v-alert-title>
            <div class="bingo-category-venues-modal__unavailable-notice-description">
              Check-ins for this category are not currently available.
              <template v-if="categoryNextAvailableDateFormatted">
                They will be
                <span class="bingo-category-venues-modal__unavailable-notice-description-date">
                  {{ categoryNextAvailableDateFormatted.replace(/^Available\\b/, 'available') }}
                </span>.
              </template>
            </div>
          </v-alert>

          <p class="bingo-category-venues-modal__description">Check in at any {{categoryName}} to fill this square.</p>
          
          <p v-if="venues.length === 0" class="bingo-category-venues-modal__empty">No venues in this category.</p>
          <div v-else class="venue-list">
          <v-card
            v-for="venue in venues"
            :key="venue.id"
            class="venue-list__card px-0 py-0"
            :class="venueCardClasses(venue)"
            variant="outlined"
            rounded="lg"
            @click="isVenueListItemDisabled(venue) ? undefined : onVenueClick(venue.id)"
          >
            <v-card-text class="venue-list__card-body d-flex align-center pa-0">
              <div class="venue-list__card-content d-flex flex-column flex-grow-1 min-width-0 justify-center">
                <span class="venue-list__name" :class="venueNameClasses(venue)">{{ venue.name }}</span>
                <!-- <span v-if="venueSubline(venue)" class="venue-list__subline">{{ venueSubline(venue) }}</span> -->
              </div>
              <div v-if="venueDistanceFormatted(venue)" class="venue-list__card-distance d-flex align-center flex-shrink-0">
                <icon class="venue-list__distance-icon" :fills="['rgba(var(--v-theme-on-surface), 0.5)']" height="13" width="13">MapPin</icon>
                <span class="venue-list__distance-text">{{ venueDistanceFormatted(venue) }}</span>
              </div>
            </v-card-text>
          </v-card>
          </div>
        </div>
      </v-card-text>      
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { getVenueDistance, formatDistance } from '@/helpers';
import { mergeBingoCellQuery } from '@/utils/bingoCellUrl';
import {
  formatNextAvailableMessage,
  getNextAvailableDateTime,
  lookupCategoryActiveDatesEntry
} from '@/utils/dateRanges';

export default {
  name: 'BingoCategoryVenuesModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    categoryName: {
      type: String,
      default: ''
    },
    categoryId: {
      type: [Number, String],
      default: null
    },
    categoryVenueIds: {
      type: Array,
      default: () => []
    },
    dealId: {
      type: [Number, String],
      default: null
    },
    filters: {
      type: Object,
      default: () => ({ categories: [], maxDistance: null })
    },
    usedVenueIds: {
      type: Array,
      default: () => []
    },
    categoryUnavailable: {
      type: Boolean,
      default: false
    },
    categoryNextAvailableDate: {
      type: String,
      default: null
    }
  },
  emits: ['update:open'],
  watch: {
    open(isOpen) {
      if (isOpen && !this.userLocation?.latitude && !this.userLocation?.longitude) {
        this.setUserLocation();
      }
    }
  },
  computed: {
    ...mapGetters('venues', { findAllVenues: 'findAll', findVenue: 'find' }),
    ...mapGetters('userLocation', { userLocation: 'getUserLocation' }),
    ...mapState('bingo', ['categoryActiveDates']),
    venues() {
      if (!this.dealId || !Array.isArray(this.categoryVenueIds) || !this.categoryVenueIds.length) {
        return [];
      }
      const ids = this.categoryVenueIds.map(id => Number(id));
      let list = this.findAllVenues(ids, this.dealId) || [];
      if (list.length === 0) {
        list = ids
          .map(id => this.findVenue(id, this.dealId))
          .filter(v => v && v.id != null);
      }
      const filters = this.filters || {};
      if (Array.isArray(filters.categories) && filters.categories.length > 0) {
        list = list.filter(venue =>
          venue?.categories?.some(c => filters.categories.includes(c.id))
        );
      }
      const hasLocation = this.userLocation?.latitude != null && this.userLocation?.longitude != null;
      if (hasLocation && filters.maxDistance != null && typeof filters.maxDistance === 'number') {
        list = list.filter(venue => {
          const venueWithCoords = {
            ...venue,
            latitude: venue?.latitude ?? venue?.lat,
            longitude: venue?.longitude ?? venue?.lng
          };
          const miles = getVenueDistance(venueWithCoords, this.userLocation);
          return miles != null && miles <= filters.maxDistance;
        });
      }
      return list;
    },
    categoryNextAvailableDateFormatted() {
      const entry = lookupCategoryActiveDatesEntry(
        this.categoryActiveDates,
        this.categoryId
      );
      if (!entry) return '';

      const d = getNextAvailableDateTime(
        entry.active_dates || [],
        entry.blackout_dates || [],
        new Date()
      );
      if (!d) return '';

      return formatNextAvailableMessage(d);
    }
  },
  methods: {
    ...mapActions('userLocation', ['setUserLocation']),
    venueSubline(venue) {
      const products = venue?.products;
      if (!Array.isArray(products) || products.length === 0) return '';
      return products.map(p => p?.name).filter(Boolean).join(', ');
    },
    venueDistanceFormatted(venue) {
      const venueWithCoords = {
        ...venue,
        latitude: venue?.latitude ?? venue?.lat,
        longitude: venue?.longitude ?? venue?.lng
      };
      const distance = getVenueDistance(venueWithCoords, this.userLocation);
      if (distance == null) return '';
      const formatted = formatDistance(distance);
      const unit = this.userLocation?.userLocale === 'en-US' ? 'mi' : 'km';
      return `${formatted} ${unit}`;
    },
    isVenueRedeemed(venue) {
      const used = this.usedVenueIds || [];
      if (used.length === 0) return false;
      const id = venue?.id != null ? Number(venue.id) : null;
      return id != null && used.some(usedId => Number(usedId) === id);
    },
    isVenueListItemDisabled(venue) {
      return this.categoryUnavailable || this.isVenueRedeemed(venue);
    },
    venueCardClasses(venue) {
      return {
        'venue-list__card--redeemed': this.isVenueRedeemed(venue),
        'venue-list__card--unavailable': this.categoryUnavailable
      };
    },
    venueNameClasses(venue) {
      return {
        'venue-list__name--redeemed': this.isVenueRedeemed(venue),
        'venue-list__name--unavailable': this.categoryUnavailable
      };
    },
    onVenueClick(venueId) {
      this.$emit('update:open', false);
      const token = window.token || this.$route.query?.t;
      const base = { ...this.$route.query };
      if (token) base.t = token;
      const pending = this.$store.state.bingo?.pendingBingoCell;
      const query =
        Array.isArray(pending) && pending.length >= 2
          ? mergeBingoCellQuery(base, pending[0], pending[1])
          : base;
      this.$router.push({
        path: `/experiences/deal/${this.dealId}/venue/${venueId}`,
        query
      });
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.bingo-category-venues-modal) {
  height: 75vh;
  top: 25vh;
}

.bingo-category-venues-modal__header {
  width: 100%;
  min-width: 0;
}

.bingo-category-venues-modal__title {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.bingo-category-venues-modal__body {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden;
  padding: 0 1rem 1rem !important;
}

.v-card-text {
  flex: none;
}

.v-card-title {
  font-size: 1.125rem !important;
  padding: 18px 20px 0 20px !important;
  min-height: 64px;
}

.bingo-category-venues-modal__description {
  font-size: .75rem;
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.60);
  padding: 0;
  line-height: 1rem !important;
  margin-bottom: 0.75rem !important;
}

.bingo-category-venues-modal__unavailable-notice {
  border-radius: .875rem;
  background-color: rgba(var(--v-theme-error), 0.05); 
  border: 1px solid rgba(var(--v-theme-error), 0.45) !important; 
  padding: .75rem !important;
  margin-bottom: .75rem !important;
}

.bingo-category-venues-modal__unavailable-notice-title {
  font-size: .75rem;
  font-weight: 600;
  line-height: 1rem;
  margin-bottom: 0.15rem;
}

.bingo-category-venues-modal__unavailable-notice-description {
  font-size: .75rem;
  font-weight: 400;
  line-height: 1rem !important;
}

.bingo-category-venues-modal__unavailable-notice :deep(.v-alert__prepend) {
  margin-right: .25rem;
}

.bingo-category-venues-modal__unavailable-notice-description-date {
    font-weight: 600;
}

.v-btn--icon.v-btn--density-default {
  width: 32px;
  height: 32px;
}

/* Single scrollable content area (description + list) for reliable iOS scroll */
.bingo-category-venues-modal__scroll {
  height: 100%;
  min-height: 200px; /* prevent collapse when flex height is unreliable (e.g. iOS) */
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding-bottom: max(1.5rem, env(safe-area-inset-bottom, 0px));
}

.bingo-category-venues-modal__empty {
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 0.5rem 0 1rem;
}

.venue-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.venue-list__card {
  cursor: pointer;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:active,
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }

  &--redeemed {
    cursor: default;
    pointer-events: none;
    opacity: 0.75;

    &:hover {
      box-shadow: none;
    }
  }

  &--unavailable {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.72;

    &:hover {
      box-shadow: none;
    }
  }
}

.venue-list__card-body {
  gap: 0;
}

.venue-list__card :deep(.v-card-text) {
  width: 100%;
  padding: .75rem !important;
  gap: 12px;
}

.venue-list__card-content {
  padding: 0;
  text-align: left;
  min-width: 0;
}

.venue-list__name {
  display: block;
  font-size: .875rem;
  line-height: 1.25rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), .75);
  word-wrap: break-word;
  overflow-wrap: break-word;

  &--redeemed {
    text-decoration: line-through;
  }

  &--unavailable {
    color: rgba(var(--v-theme-on-surface), 0.45);
  }
}

.venue-list__subline {
  display: block;
  margin-top: 2px;
  font-size: 0.8125rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.venue-list__card-distance {
  gap: 4px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.75rem;
}

.venue-list__distance-icon {
  flex-shrink: 0;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.venue-list__distance-text {
  white-space: nowrap;
}

.category-close-btn.v-btn--size-large {
  height: 40px;
  min-width: 71px;
  width: 100%;
  padding: 0 18px;
  font-size: 0.875rem;
}

:deep(.category-close-btn .v-btn__content) {
  top: 1px;
  left: -1px;
  position: relative;
  display: inline-block;
}

.bingo-category-venues-modal {
    height: 85vh !important;
    max-height: 85vh !important;
    top: unset !important;
    border-top-left-radius: 1rem !important;
    border-top-right-radius: 1rem !important;
    border-bottom-left-radius: 0px !important;
    border-bottom-right-radius: 0px !important;
    overflow: hidden;
}

.bingo-category-venues-modal__title {
    color: #1b2632;
    font-size: 1rem !important;
    line-height: 1.5rem !important;
    margin: 0 !important;
}

.v-card .v-card-title {
    padding: 0 !important;
    min-height: auto !important;
    margin-bottom: .75rem;
}

.v-card {
    padding: 1rem;
}

.v-card-text {
    padding: 0 !important;
    margin: 0 !important;
}

span.venue-list__name {
    font-size: .875rem !important;
    line-height: 1.25rem !important;
}

span.venue-list__distance-text {
    font-size: .75rem !important;
    line-height: 1rem !important;
    color: #737f8c !important;
}

.venue-list__card-distance svg path {
    fill: rgba(var(--v-theme-on-surface), 0.4)  !important;
    stroke: rgba(var(--v-theme-on-surface), 0.4) !important;
}

.v-card-text.venue-list__card-body {
    padding: .75rem;
}

.venue-list__card {
    border-color: #dae0e7 !important;
}

.venue-list {
    gap: 8px !important;
}

</style>
